import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useStoreContext } from 'features/store';
import StoreConfirmationModal from 'features/store/components/store-confirmation-modal';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { Dimensions, GestureResponderEvent } from 'react-native';

import ActionButton from 'components/action-button';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import SignUpToContinueActionLink from 'experiments/checkout-v2/control/pages/cart/payment/sign-up-to-continue-action-link';
import { CheckOutStickyFooter } from 'experiments/checkout-v2/control/pages/cart/payment/sticky-footer';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import useFoodware from 'hooks/use-foodware';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import useTraceLoading from 'hooks/use-trace-loading';
import { LegacyCondimentsOnlyModal } from 'pages/cart/legacy-condiments-only-modal';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useSmartAlertsContext } from 'state/smart-alert';
import { useUIContext } from 'state/ui';
import { isLegacyCondimentsOnlyCart } from 'utils/legacy-condiments';
import { TraceName, stopTrace } from 'utils/performance/trace';
import { routes } from 'utils/routing';

import { CartPriceTooLow, SignUpToContinueDisclaimer, StoreUnavailableMessage } from './styled';
import { IPaymentProps } from './types';
import { logCartContinue } from './utils';

const SELECT_NEW_RESTAURANT_TO_CONTINUE = 'Select a new restaurant to continue.';

// this is not payment related this is actually the cart footer
const Payment: React.FC<React.PropsWithChildren<IPaymentProps>> = ({
  disabled = false,
  serverOrder,
  offerIsValid,
  scrollViewRef,
  phoneNumberInputRef,
  shouldSkipPriceOrder,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const {
    priceOrderFailure,
    isOrderPriceLoading,
    isOrderPricingRetryInProgress,
    didOrderPricingRetryFail,
    isPricingFailureRecoverable,
    tryPricing,
  } = useCartContext();
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);

  const { calculateCartTotal, cartEntries } = useCart();
  const { featureFoodware } = useFoodware();
  const { sections: addOnSections } = useFeatureMenuAddOn({ ignoreEnableAddExtras: true });

  const { alertOrderDeliveryMinimum } = useOrderContext();
  const serviceMode = useServiceModeLive();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const { store } = useStoreContext();
  const { isStoreAvailable } = useStoreAvailabilityFns();
  const { currentStoreDate } = useSmartAlertsContext();
  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [showCondimentsOnlyModal, setShowCondimentsOnlyModal] = useState(false);
  const [hasManualPricingBeenAttempted, setHasManualPricingAttempted] = useState(false);

  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);

  const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  useEffect(alertOrderDeliveryMinimum, [alertOrderDeliveryMinimum]);

  useEffect(() => {
    if (priceOrderFailure && didOrderPricingRetryFail) {
      setIsCheckoutErrorsModalOpen(true);
    }
  }, [priceOrderFailure, didOrderPricingRetryFail]);

  const checkoutPriceMinimum = isDelivery ? checkoutDeliveryPriceMinimum : 0;

  const cartTotal = calculateCartTotal();
  const orderTotal = isSignUpAfterCartEnabled ? cartTotal : serverOrder?.cart?.totalCents ?? 0;

  const cartSubTotal =
    isSignUpAfterCartEnabled || shouldSkipPriceOrder
      ? cartTotal
      : Number(serverOrder?.cart?.subTotalCents);
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;
  const foodwareItemIds = (featureFoodware?.items ?? []).flatMap(item =>
    item?._id ? [item._id] : []
  );
  const isCondimentsOnlyCart = isLegacyCondimentsOnlyCart(
    cartEntries,
    addOnSections,
    foodwareItemIds
  );

  const [StoreConfirmationDialog, , , dismissStoreConfirmationDialog] = useDialogModal({
    showCancel: true,
    Component: StoreConfirmationModal,
    modalAppearanceEventMessage: 'Confirmation Modal: Store Location',
  });

  const { shouldConfirmStoreOnCheckout } = useUIContext();

  const isLoading = (!shouldSkipPriceOrder && isOrderPriceLoading) || isLoadingOffers;

  const onStoreConfirmation = () => {
    dismissStoreConfirmationDialog();
    shouldConfirmStoreOnCheckout(true);
  };

  const { getErrorMessages } = useGetPaymentErrors({ cartPriceTooLow });

  const paymentErrorMessages = getErrorMessages(
    didOrderPricingRetryFail
      ? {
          unableToProcessOrderError: hasManualPricingBeenAttempted,
          pricedAndAvailableError: !hasManualPricingBeenAttempted,
        }
      : {
          invalidOfferError: !offerIsValid,
        }
  );

  const scrolledToFocusedInput = () => {
    phoneNumberInputRef?.current?.measure(
      (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
        const { height: windowHeight } = Dimensions.get('window');
        const scrollToYValue = pageY > 0 ? pageY : windowHeight - Math.abs(pageY);
        scrollViewRef?.current?.scrollTo({
          y: scrollToYValue,
          animated: true,
        });

        // Workaround to open keyboard on input focus.
        setTimeout(() => {
          phoneNumberInputRef?.current?.blur();
          phoneNumberInputRef?.current?.focus();
        }, 50);
      }
    );
  };

  useTraceLoading({
    name: 'Continue',
    isLoading,
  });

  const isLoadingWasTrueRef = useRef(false);
  useEffect(() => {
    if (isLoading) {
      // sometimes isLoading === false to start, guard against this
      isLoadingWasTrueRef.current = true;
    }
    if (!isLoading && isLoadingWasTrueRef.current && pricedOrderId) {
      stopTrace(TraceName.CART_LOAD_TO_CONTINUE_LOADED, { rbiOrderId: pricedOrderId });
    }
  }, [isLoading, pricedOrderId]);

  const hasErrors = !!paymentErrorMessages.length;
  const isStoreOrderable =
    !!serviceMode && isStoreAvailable(store, serviceMode, currentStoreDate ?? store.localTime);
  const isContinueDisabled = disabled || !isStoreOrderable;
  const shouldShowStoreUnavailableMessage = !isStoreOrderable;

  const ContinueButton = useMemo(() => {
    let buttonText = t('continue');

    if (isOrderPricingRetryInProgress) {
      buttonText = t('thisMayTakeAFewSeconds');
    } else if (isLoading) {
      buttonText = t('loadingYourOrder');
    }

    if (hasErrors) {
      const handleErrorContinueClick = () => {
        if (isCondimentsOnlyCart) {
          setShowCondimentsOnlyModal(true);
          return;
        }

        logCartContinue();
        setIsCheckoutErrorsModalOpen(true);
      };

      return (
        <ActionButton
          onPress={handleErrorContinueClick}
          fullWidth
          isLoading={isLoading}
          noLogOnLongLoading
          disabled={
            isContinueDisabled ||
            !offerIsValid ||
            (!isCondimentsOnlyCart && cartPriceTooLow) ||
            shouldSkipPriceOrder ||
            isLoading
          }
        >
          {buttonText}
        </ActionButton>
      );
    }

    const handleContinuePress = (e: GestureResponderEvent) => {
      // if loading do nothing
      if (isLoading) {
        e?.preventDefault();
        e?.stopPropagation();
        return;
      }

      logCartContinue();

      if (isCondimentsOnlyCart) {
        setShowCondimentsOnlyModal(true);
        return;
      }
    };

    return (
      <ActionButton
        onPress={handleContinuePress}
        to={isCondimentsOnlyCart ? undefined : routes.cartCheckout}
        fullWidth
        isLoading={isLoading}
        noLogOnLongLoading
        disabled={isContinueDisabled || (!isCondimentsOnlyCart && cartPriceTooLow) || isLoading}
      >
        {buttonText}
      </ActionButton>
    );
  }, [
    isLoading,
    isContinueDisabled,
    offerIsValid,
    cartPriceTooLow,
    hasErrors,
    isCondimentsOnlyCart,
    shouldSkipPriceOrder,
    isOrderPricingRetryInProgress,
  ]);

  const cartPriceTooLowMessage = t('deliveryMinumumNotReached', {
    min: formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
  });

  // TODO FIX technically message visibility should be tied to isLoading (it flashes "minimum not reached" on page load every time)
  // but trying to not change any page logic with these error message additions to reduce risk
  // bug here: https://rbictg.atlassian.net/browse/BKPE-2611
  useLogUserErrorMessage({
    message: cartPriceTooLowMessage,
    logWhenTrue: cartPriceTooLow && !isLoading,
    severity: LogUserErrorSeverity.Recoverable,
  });

  const CTAButton = isSignUpAfterCartEnabled ? (
    <>
      <SignUpToContinueDisclaimer>{t('signUpToContinueDisclaimer')}</SignUpToContinueDisclaimer>
      <SignUpToContinueActionLink />
    </>
  ) : (
    <>
      {shouldShowStoreUnavailableMessage && (
        <StoreUnavailableMessage>{SELECT_NEW_RESTAURANT_TO_CONTINUE}</StoreUnavailableMessage>
      )}
      {ContinueButton}
    </>
  );

  const priceTooLowCmp = cartPriceTooLow && !isLoading && (
    <CartPriceTooLow>{cartPriceTooLowMessage}</CartPriceTooLow>
  );

  const attemptManualOrderPricing = () => {
    setHasManualPricingAttempted(true);
    tryPricing({ isRepriceAttempt: true });
    setIsCheckoutErrorsModalOpen(false);
  };

  const shouldAllowManualPricingAttempt =
    isPricingFailureRecoverable && didOrderPricingRetryFail && !hasManualPricingBeenAttempted;

  return (
    <>
      <CheckOutStickyFooter
        priceTooLowCmp={priceTooLowCmp}
        CTAButton={CTAButton}
        isLoading={isLoading}
        orderTotal={orderTotal}
      />

      {/* modals */}
      <StoreConfirmationDialog
        onConfirm={onStoreConfirmation}
        onDismiss={dismissStoreConfirmationDialog}
      />
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          {...(shouldAllowManualPricingAttempt
            ? {
                onCtaPress: attemptManualOrderPricing,
                ctaText: t('tryAgain'),
                heading: t('oopsSomethingWentWrong'),
              }
            : {
                heading: t('unableToProcessOrder'),
              })}
          checkoutErrors={paymentErrorMessages}
          onClose={() => {
            setIsCheckoutErrorsModalOpen(false);
            scrolledToFocusedInput();
          }}
        />
      )}
      <LegacyCondimentsOnlyModal
        isOpen={showCondimentsOnlyModal}
        onClose={() => setShowCondimentsOnlyModal(false)}
      />
    </>
  );
};

export default Payment;
